import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import css from './index.module.scss';
import {
  DEFAULT_CURRENCY,
  GENERIC_INFORMATION_DEFAULT_VALUE,
  NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE,
  getCountryOfOriginOption,
  getCountryOfOriginPayload,
  getMaterialLocationCountryOption,
  getMaterialLocationCountryPayload,
  getProductDetailsPayload,
  toQuoteProductOption
} from '../helper';
import EnquiryGenericInformation from '../generic-information';
import {
  IFilterState,
  INonConsolidateFormData,
  INonConsolidateQuote,
  IQuoteFormState,
  IQuotePrefill,
  IWeightChildOption,
  IWeightSystemOption
} from '@helpers/types/quotes';
import { useEffect } from 'react';
import { CURRENCY, MARKET_TYPE, QUOTE_CONTAINER_TYPE, WEIGHT_SYSTEM } from '@helpers/constants';
import CostCalculator from '../cost-calculator';
import { Button } from '@components/base';
import { yupResolver } from '@hookform/resolvers/yup';
import { nonConsolidationQuoteValidation } from '@helpers/yup/add-quote.schema';
import NonConsolidationQuote from './components/non-consolidation';
import { useNavigate } from 'react-router-dom';

interface INonConsolidationProps {
  quoteFormState: IQuoteFormState;
  setQuoteFormState: React.Dispatch<React.SetStateAction<IQuoteFormState>>;
  filterState: IFilterState;
  filterProductOptions: (event: any, param?: any) => Promise<void>;
  filterPorts: (event?: any, param?: any, fieldType?: string) => Promise<void>;
  data?: any;
  prefill?: IQuotePrefill | null;
  onSubmitClose: () => void;
  saveAsDraft: (reqBody: any) => Promise<void>;
  editEnquirySubmit: (enquiryId: string, reqBody: any) => Promise<void>;
  fetchFormCurrentValue: (data: any) => void;
}

const InternationalNonConsolidation = (props: INonConsolidationProps) => {
  const navigate = useNavigate();

  const {
    quoteFormState,
    setQuoteFormState,
    filterState,
    filterProductOptions,
    filterPorts,
    data,
    prefill,
    onSubmitClose,
    saveAsDraft,
    editEnquirySubmit,
    fetchFormCurrentValue
  } = props;

  const { fcl, mtt, consolidation } = filterState;

  const {
    selectedWeightSystemChildOptions,
    selectedCurrency,
    productOptions,
    portOfLoadingOptions,
    portOfDischargeOptions,
    enquiryId,
    duplicateEnquiry,
    activeStep,
    firstFormData,
    isDraft
  } = quoteFormState;

  const enquiryForm = useForm<INonConsolidateFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(nonConsolidationQuoteValidation),
    defaultValues: prefill?.quoteForm ?? {
      genericInformation: GENERIC_INFORMATION_DEFAULT_VALUE,
      quote: [NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE]
    }
  });

  const { control, handleSubmit, getValues, reset } = enquiryForm;

  const {
    fields: fields,
    append: append,
    remove: remove
  } = useFieldArray({
    name: 'quote',
    control
  });

  const handleAddQuote = () => {
    append(NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE);
  };

  const handleDuplicateQuote = (index: number) => {
    const quoteToDuplicate = enquiryForm.getValues(`quote.${index}`);
    append({
      ...quoteToDuplicate,
      isExpanded: true
    });
    const updatedQuotes = enquiryForm
      .getValues('quote')
      .map((item: INonConsolidateQuote, i: number, arr: any) => {
        if (i === arr.length - 1) {
          return { ...item, isExpanded: true };
        } else {
          return { ...item, isExpanded: false };
        }
      });
    enquiryForm.setValue('quote', updatedQuotes);
  };

  const constructReqBody = (data: any, isDraftAction = false) => {
    const reqBody: any = {
      currency_type_one: data.genericInformation.currency1?.value,
      currency_type_two: data.genericInformation.currency2?.value,
      fx_rate: data.genericInformation?.fxRate,
      weight_system: data.genericInformation.weightSystem?.value,
      deal_name: data.genericInformation?.draftDealName,
      primary_unit: data.genericInformation.primaryUnit?.value,
      is_draft: isDraftAction,
      market_type: MARKET_TYPE.INTERNATIONAL,
      quotes: (data.quote || []).map((quote: INonConsolidateQuote) => {
        const incoterm: string = quote.incoTerms?.value;
        let pol = null;
        let pod = null;
        let dealIncotermLocation = null;

        if (incoterm === 'FOB') {
          pol = quote.portOfLoading?.value ?? null;
        } else if (['CIF', 'CNF', 'CFR'].includes(incoterm)) {
          pod = quote.portOfDischarge?.value ?? null;
        } else if (incoterm === 'EXW') {
          dealIncotermLocation = quote.incoTermsLocation ?? null;
        } else if (['DDP', 'DAP'].includes(incoterm)) {
          dealIncotermLocation = quote.incoTermsLocation ?? null;
        }

        return {
          product_details: getProductDetailsPayload(quote.productName),
          origin: getCountryOfOriginPayload(quote.countryOfOrigin),
          material_location_country: getMaterialLocationCountryPayload(
            quote.materialLocationCountry
          ),
          quantity: quote?.dealQuantity,
          quantity_unit: quote.dealQuantityUnit?.value,
          packaging: quote.productPackaging,
          container_size: quote.containerSize?.value,
          costs: {
            base_price: {
              currency: quote.base_price?.currency?.value,
              value: quote.base_price?.value,
              cost_id: quote.base_price?.cost_id,
              unit_of_weight: quote.base_price?.weightUnit?.value
            },
            ex_works_price: {
              currency: quote.final_exWorks_price?.currency?.value,
              value: quote.final_exWorks_price?.value,
              cost_id: quote.final_exWorks_price?.cost_id,
              unit_of_weight: quote.final_exWorks_price?.weightUnit?.value
            },
            fob_price: {
              currency: quote.fob_price?.currency?.value,
              value: quote.fob_price?.value,
              cost_id: quote.fob_price?.cost_id,
              unit_of_weight: quote.fob_price?.weightUnit?.value
            },
            cfr_price: {
              currency: quote.cfr_price?.currency?.value,
              value: quote.cfr_price?.value,
              cost_id: quote.cfr_price?.cost_id,
              unit_of_weight: quote.cfr_price?.weightUnit?.value
            },
            cif_price: {
              currency: quote.cif_price?.currency?.value,
              value: quote.cif_price?.value,
              cost_id: quote.cif_price?.cost_id,
              unit_of_weight: quote.cif_price?.weightUnit?.value
            },
            ddp_price: {
              currency: quote.ddp_price?.currency?.value,
              value: quote.ddp_price?.value,
              cost_id: quote.ddp_price?.cost_id,
              unit_of_weight: quote.ddp_price?.weightUnit?.value
            },
            total_local_freight: {
              currency: quote.totalLocalFreight?.currency?.value,
              value: quote.totalLocalFreight?.value,
              cost_id: quote.totalLocalFreight?.cost_id
            },
            total_port_and_clearance_expenses: {
              currency: quote.totalPortAndClearanceExpenses?.currency?.value,
              value: quote.totalPortAndClearanceExpenses?.value,
              cost_id: quote.totalPortAndClearanceExpenses?.cost_id
            },
            total_freight: {
              currency: quote.totalFreight?.currency?.value,
              value: quote.totalFreight?.value,
              cost_id: quote.totalFreight?.cost_id
            },
            switch_bl_cost: quote.switchBlCost
              ? {
                  currency: quote.switchBlCost?.currency?.value,
                  value: quote.switchBlCost?.value,
                  cost_id: quote.switchBlCost?.cost_id
                }
              : null,
            final_price: {
              currency: data.genericInformation.currency1.value,
              value: quote.finalQuote_selling_price?.value,
              cost_id: quote.finalQuote_selling_price?.cost_id,
              unit_of_weight: quote.finalQuote_selling_price?.weightUnit?.value
            },
            additional_cost: {
              currency: quote.additionalCost?.currency?.value,
              value: quote.additionalCost?.value,
              cost_id: quote.additionalCost?.cost_id
            },
            isf_filing: {
              currency: quote.isfFiling?.currency?.value,
              value: quote.isfFiling?.value,
              cost_id: quote.isfFiling?.cost_id
            },
            transportation_cost: {
              currency: quote.drayageCharges?.currency?.value,
              value: quote.drayageCharges?.value,
              cost_id: quote.drayageCharges?.cost_id
            },
            warehousing_cost: {
              currency: quote.warehousingCost?.currency?.value,
              value: quote.warehousingCost?.value,
              cost_id: quote.warehousingCost?.cost_id
            }
          },
          final_price_weight_unit: quote.finalQuote_selling_price?.weightUnit?.value,
          moq: quote.moq,
          moq_unit: quote.moqUnit?.value,
          weight_one_container: quote.totalWeight,
          weight_one_container_unit: quote.totalWeightUnit.value,
          insurance_cost: quote.insuranceCost,
          finance_cost: quote.financeCost,
          profit_margin: quote.profitMarginExpected,
          discount: quote.discount,
          tariff: quote.tariff,
          deal_incoterm: quote.incoTerms?.value,
          deal_incoterm_location: dealIncotermLocation,
          pol: pol,
          pod: pod,
          is_draft: false,
          deal_status: null,
          enquiry: enquiryId,
          quote_id: quote.quote_id,
          custom_duty: quote.customClearance,
          zip_code: quote.incoTermsZipCode
        };
      })
    };

    reqBody.sourcing_type = mtt ? 'MTT' : 'NON_MTT';
    reqBody.container_type = fcl ? 'FCL' : 'LCL';
    reqBody.is_consolidation = consolidation;

    return reqBody;
  };

  const formWatch = useWatch({
    control
  });

  useEffect(() => {
    if (formWatch) {
      const reqBody = constructReqBody(formWatch, true);
      fetchFormCurrentValue(reqBody);
    }
  }, [formWatch]);

  const onFormSubmit = (data: INonConsolidateFormData, isDraftAction = false) => {
    const currentFormData = getValues();
    const reqBody: any = constructReqBody(data, isDraftAction);
    if (isDraft && !isDraftAction) {
      reqBody.convert_from_draft_to_enquiry = true;
    }

    if (!isDraft && isDraftAction) {
      saveAsDraft(reqBody);
      return;
    }

    if (isDraft && isDraftAction) {
      editEnquirySubmit(enquiryId ?? '', reqBody);
      onSubmitClose();
      navigate('/quote?activeTab=cost_calculator&page=1');
      return;
    }

    setQuoteFormState((prevState: IQuoteFormState) => ({
      ...prevState,
      firstFormReqBody: reqBody,
      activeStep: activeStep + 1,
      firstFormData: currentFormData
    }));
  };

  const primaryUnitWatch = useWatch({
    control,
    name: 'genericInformation.primaryUnit'
  });

  const currency1Watch = useWatch({
    control,
    name: `genericInformation.currency1`
  });

  const fxRateWatch = useWatch({
    control,
    name: `genericInformation.fxRate`
  });

  const quoteWatch = useWatch({
    control,
    name: 'quote'
  });

  useEffect(() => {
    if (firstFormData) {
      reset(firstFormData);

      const selectedChildWeights = WEIGHT_SYSTEM.find(
        (item: IWeightSystemOption) =>
          item.value === firstFormData.genericInformation.weightSystem.value
      )?.childOptions;

      setQuoteFormState((prevState: IQuoteFormState) => ({
        ...prevState,
        selectedWeightSystemChildOptions: selectedChildWeights || [],
        selectedCurrency: [
          CURRENCY.find(
            (item) => item.value === firstFormData.genericInformation.currency1.value
          ) || DEFAULT_CURRENCY,
          CURRENCY.find(
            (item) => item.value === firstFormData.genericInformation.currency2.value
          ) || DEFAULT_CURRENCY
        ]
      }));
    } else if (data) {
      const selectedChildWeights = WEIGHT_SYSTEM.find(
        (item: IWeightSystemOption) => item.value === data.weight_system
      )?.childOptions;

      const primaryCurrency = CURRENCY.find((item) => item.value === data.currency_type_one);
      const primaryUnit = selectedChildWeights?.find(
        (item: IWeightChildOption) => item.value === data?.primary_unit
      );

      enquiryForm.reset({
        genericInformation: {
          currency1: CURRENCY.find((item) => item.value === data.currency_type_one),
          currency2: CURRENCY.find((item) => item.value === data.currency_type_two),
          fxRate: data.fx_rate,
          weightSystem: WEIGHT_SYSTEM?.find(
            (item: IWeightSystemOption) => item.value === data.weight_system
          ),
          draftDealName: data?.deal_name,
          primaryUnit: selectedChildWeights?.find(
            (item: IWeightChildOption) => item.value === data?.primary_unit
          )
        },
        quote: (data?.quotes || []).map((quote: any) => {
          return {
            quote_id: quote.quote_id,
            isExpanded: true,
            productName: toQuoteProductOption(quote.product_details),
            containerSize:
              QUOTE_CONTAINER_TYPE.find((item) => item.value === quote.container_size) ?? null,
            productPackaging: quote?.packaging,
            dealQuantity: quote?.quantity,
            dealQuantityUnit: quote?.quantity_unit
              ? selectedChildWeights?.find(
                  (item: IWeightChildOption) => item.value === quote.quantity_unit
                )
              : primaryUnit,
            base_price: {
              value: quote.base_price ? quote.base_price?.value : 0,
              currency: quote?.base_price
                ? CURRENCY.find((item) => item.value === quote?.base_price?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.base_price?.cost_id,
              manualOverride: true,
              weightUnit: quote?.base_price?.unit_of_weight
                ? selectedChildWeights?.find(
                    (item: IWeightChildOption) => item.value === quote?.base_price?.unit_of_weight
                  )
                : primaryUnit
            },
            fob_price: {
              value: quote.fob_price ? quote.fob_price?.value : 0,
              currency: quote?.fob_price
                ? CURRENCY.find((item) => item.value === quote?.fob_price?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.fob_price?.cost_id,
              manualOverride: true,
              weightUnit: quote?.fob_price?.unit_of_weight
                ? selectedChildWeights?.find(
                    (item: IWeightChildOption) => item.value === quote?.fob_price?.unit_of_weight
                  )
                : primaryUnit
            },
            cfr_price: {
              value: quote.cfr_price ? quote.cfr_price?.value : 0,
              currency: quote?.cfr_price
                ? CURRENCY.find((item) => item.value === quote?.cfr_price?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.cfr_price?.cost_id,
              manualOverride: true,
              weightUnit: quote?.cfr_price?.unit_of_weight
                ? selectedChildWeights?.find(
                    (item: IWeightChildOption) => item.value === quote?.cfr_price?.unit_of_weight
                  )
                : primaryUnit
            },
            cif_price: {
              value: quote.cif_price ? quote.cif_price?.value : 0,
              currency: quote?.cif_price
                ? CURRENCY.find((item) => item.value === quote?.cif_price?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.cif_price?.cost_id,
              manualOverride: true,
              weightUnit: quote?.cif_price?.unit_of_weight
                ? selectedChildWeights?.find(
                    (item: IWeightChildOption) => item.value === quote?.cif_price?.unit_of_weight
                  )
                : primaryUnit
            },
            moq: quote?.moq,
            moqUnit: quote?.moq_unit
              ? selectedChildWeights?.find(
                  (item: IWeightChildOption) => item.value === quote?.moq_unit
                )
              : primaryUnit,
            totalWeight: quote?.weight_one_container,
            totalWeightUnit: quote?.weight_one_container_unit
              ? selectedChildWeights?.find(
                  (item: IWeightChildOption) => item.value === quote?.weight_one_container_unit
                )
              : primaryUnit,
            totalLocalFreight: {
              value: quote.total_local_freight ? quote.total_local_freight?.value : 0,
              currency: quote.total_local_freight
                ? CURRENCY.find((item) => item.value === quote?.total_local_freight?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote.total_local_freight?.cost_id
            },
            totalPortAndClearanceExpenses: {
              value: quote.total_port_and_clearance_expenses
                ? quote.total_port_and_clearance_expenses?.value
                : 0,
              currency: quote?.total_port_and_clearance_expenses
                ? CURRENCY.find(
                    (item) => item.value === quote.total_port_and_clearance_expenses?.currency
                  )
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.total_port_and_clearance_expenses?.cost_id
            },
            totalFreight: {
              value: quote.total_freight ? quote.total_freight?.value : 0,
              currency: quote?.total_freight
                ? CURRENCY.find((item) => item.value === quote?.total_freight?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.total_freight?.cost_id
            },
            portOfLoading: quote?.pol
              ? {
                  label: quote.pol?.name,
                  value: quote.pol?.id
                }
              : null,
            portOfDischarge: quote?.pod
              ? {
                  label: quote.pod?.name,
                  value: quote.pod?.id
                }
              : null,
            incoTermsLocation: quote?.deal_incoterm_location,
            insuranceCost: quote?.insurance_cost,
            additionalCost: {
              value: quote.additional_cost ? quote.additional_cost?.value : 0,
              currency: quote?.additional_cost
                ? CURRENCY.find((item) => item?.value === quote?.additional_cost?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.additional_cost?.cost_id
            },
            financeCost: quote?.finance_cost,
            profitMarginExpected: quote?.profit_margin,
            discount: quote?.discount,
            tariff: quote?.tariff,
            incoTerms: quote?.deal_incoterm
              ? {
                  value: quote?.deal_incoterm,
                  label: quote?.deal_incoterm
                }
              : null,
            switchBlCost: {
              value: quote.switch_bl_cost ? quote.switch_bl_cost?.value : 0,
              currency: quote?.switch_bl_cost
                ? CURRENCY.find((item) => item.value === quote?.switch_bl_cost?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.switch_bl_cost?.cost_id
            },
            final_exWorks_price: {
              value: quote.ex_works_price ? quote.ex_works_price?.value : 0,
              currency: quote?.ex_works_price
                ? CURRENCY.find((item) => item.value === quote?.ex_works_price?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.ex_works_price?.cost_id,
              manualOverride: true,
              weightUnit: quote?.ex_works_price?.unit_of_weight
                ? selectedChildWeights?.find(
                    (item: IWeightChildOption) =>
                      item.value === quote?.ex_works_price?.unit_of_weight
                  )
                : primaryUnit
            },
            finalQuote_selling_price: {
              value: quote.final_price ? quote.final_price?.value : 0,
              currency: quote?.final_price
                ? CURRENCY.find((item) => item.value === quote?.final_price?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.final_price?.cost_id,
              weightUnit: quote?.final_price?.unit_of_weight
                ? selectedChildWeights?.find(
                    (item: IWeightChildOption) => item.value === quote?.final_price?.unit_of_weight
                  )
                : primaryUnit,
              manualOverride: true
            },
            countryOfOrigin: getCountryOfOriginOption(quote?.origin, quote?.origin_display_value),
            materialLocationCountry: getMaterialLocationCountryOption(
              quote?.material_location_country,
              quote?.material_location_country_display_value
            ),
            drayageCharges: {
              value: quote.transportation_cost ? quote.transportation_cost?.value : 0,
              currency: quote?.transportation_cost
                ? CURRENCY.find((item) => item.value === quote?.transportation_cost?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.transportation_cost?.cost_id
            },
            isfFiling: {
              value: quote.isf_filing ? quote.isf_filing?.value : 0,
              currency: quote?.isf_filing
                ? CURRENCY.find((item) => item.value === quote?.isf_filing?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.isf_filing?.cost_id
            },
            warehousingCost: {
              value: quote.warehousing_cost ? quote.warehousing_cost?.value : 0,
              currency: quote?.warehousing_cost
                ? CURRENCY.find((item) => item.value === quote?.warehousing_cost?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.warehousing_cost?.cost_id
            },
            customClearance: quote.custom_duty,
            ddp_price: {
              value: quote.ddp_price ? quote.ddp_price?.value : 0,
              currency: quote?.ddp_price
                ? CURRENCY.find((item) => item.value === quote?.ddp_price?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.ddp_price?.cost_id,
              manualOverride: true,
              weightUnit: quote?.ddp_price?.unit_of_weight
                ? selectedChildWeights?.find(
                    (item: IWeightChildOption) => item.value === quote?.ddp_price?.unit_of_weight
                  )
                : primaryUnit
            },
            incoTermsZipCode: quote.zip_code
          };
        })
      });
    }
  }, [data]);

  return (
    <>
      <FormProvider {...enquiryForm}>
        <form
          noValidate
          onSubmit={handleSubmit((data) => onFormSubmit(data))}
          className={css.mainWrapper}
        >
          <CostCalculator
            filters={filterState}
            weightOptions={selectedWeightSystemChildOptions}
            currency={currency1Watch}
            primaryUnitWatch={primaryUnitWatch}
            quote={quoteWatch}
          />
          <EnquiryGenericInformation
            setQuoteFormState={setQuoteFormState}
            selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
          />

          {fields.map((item: any, index: number) => {
            const handleDeleteClick = () => {
              remove(index);
            };
            return (
              <NonConsolidationQuote
                key={index}
                index={index}
                isEnquiry={true}
                isExpanded={`quote.${index}.isExpanded`}
                productName={`quote.${index}.productName`}
                productPackaging={`quote.${index}.productPackaging`}
                dealQuantity={`quote.${index}.dealQuantity`}
                dealQuantityUnit={`quote.${index}.dealQuantityUnit`}
                basePrice={`quote.${index}.base_price.value`}
                basePriceCurrency={`quote.${index}.base_price.currency`}
                basePriceWeightUnit={`quote.${index}.base_price.weightUnit`}
                fobPrice={`quote.${index}.fob_price.value`}
                fobPriceCurrency={`quote.${index}.fob_price.currency`}
                fobPriceWeightUnit={`quote.${index}.fob_price.weightUnit`}
                fobPriceManualOverride={`quote.${index}.fob_price.manualOverride`}
                ddpPrice={`quote.${index}.ddp_price.value`}
                ddpPriceCurrency={`quote.${index}.ddp_price.currency`}
                ddpPriceWeightUnit={`quote.${index}.ddp_price.weightUnit`}
                ddpPriceManualOverride={`quote.${index}.ddp_price.manualOverride`}
                moq={`quote.${index}.moq`}
                moqUnit={`quote.${index}.moqUnit`}
                containerSize={`quote.${index}.containerSize`}
                totalWeight={`quote.${index}.totalWeight`}
                totalWeightUnit={`quote.${index}.totalWeightUnit`}
                totalLocalFreight={`quote.${index}.totalLocalFreight.value`}
                totalLocalFreightCurrency={`quote.${index}.totalLocalFreight.currency`}
                totalPortAndClearanceExpenses={`quote.${index}.totalPortAndClearanceExpenses.value`}
                totalPortAndClearanceExpensesCurrency={`quote.${index}.totalPortAndClearanceExpenses.currency`}
                totalFreight={`quote.${index}.totalFreight.value`}
                totalFreightCurrency={`quote.${index}.totalFreight.currency`}
                portOfLoading={`quote.${index}.portOfLoading`}
                portOfDischarge={`quote.${index}.portOfDischarge`}
                insuranceCost={`quote.${index}.insuranceCost`}
                additionalCost={`quote.${index}.additionalCost.value`}
                additionalCostCurrency={`quote.${index}.additionalCost.currency`}
                financeCost={`quote.${index}.financeCost`}
                profitMarginExpected={`quote.${index}.profitMarginExpected`}
                discount={`quote.${index}.discount`}
                tariff={`quote.${index}.tariff`}
                incoTerms={`quote.${index}.incoTerms`}
                incoTermsLocation={`quote.${index}.incoTermsLocation`}
                incoTermsZipCode={`quote.${index}.incoTermsZipCode`}
                switchBlCost={`quote.${index}.switchBlCost.value`}
                switchBLCostCurrency={`quote.${index}.switchBlCost.currency`}
                cifPrice={`quote.${index}.cif_price.value`}
                cfrPrice={`quote.${index}.cfr_price.value`}
                cifPriceWeightUnit={`quote.${index}.cif_price.weightUnit`}
                cfrPriceWeightUnit={`quote.${index}.cfr_price.weightUnit`}
                cifPriceManualOverride={`quote.${index}.cif_price.manualOverride`}
                cfrPriceManualOverride={`quote.${index}.cfr_price.manualOverride`}
                exWorksPriceManualOverride={`quote.${index}.final_exWorks_price.manualOverride`}
                cifPriceCurrency={`quote.${index}.cif_price.currency`}
                cfrPriceCurrency={`quote.${index}.cfr_price.currency`}
                finalExWorksPrice={`quote.${index}.final_exWorks_price.value`}
                finalExWorksPriceWeightUnit={`quote.${index}.final_exWorks_price.weightUnit`}
                finalExWorksPriceCurrency={`quote.${index}.final_exWorks_price.currency`}
                finalQuoteSellingPrice={`quote.${index}.finalQuote_selling_price.value`}
                finalQuoteSellingPriceWeightUnit={`quote.${index}.finalQuote_selling_price.weightUnit`}
                countryOfOrigin={`quote.${index}.countryOfOrigin`}
                materialLocationCountry={`quote.${index}.materialLocationCountry`}
                drayageCharge={`quote.${index}.drayageCharges.value`}
                drayageChargeCurrency={`quote.${index}.drayageCharges.currency`}
                isfFiling={`quote.${index}.isfFiling.value`}
                isfFilingCurrency={`quote.${index}.isfFiling.currency`}
                warehousingCostPrice={`quote.${index}.warehousingCost.value`}
                warehousingCostCurrency={`quote.${index}.warehousingCost.currency`}
                customClearance={`quote.${index}.customClearance`}
                handleDeleteClick={handleDeleteClick}
                fieldLength={fields.length}
                handleDuplicateQuote={handleDuplicateQuote}
                filterState={filterState}
                handleAddFields={handleAddQuote}
                selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
                selectedCurrency={selectedCurrency}
                filterProductOptions={filterProductOptions}
                productOptions={productOptions}
                filterPorts={filterPorts}
                portOfLoadingOptions={portOfLoadingOptions}
                portOfDischargeOptions={portOfDischargeOptions}
                primaryUnitWatch={primaryUnitWatch}
                currency1Watch={currency1Watch}
                fxRateWatch={fxRateWatch}
              />
            );
          })}
          <Button
            variant="outlined-secondary"
            onClick={() => {
              handleAddQuote();
            }}
          >
            Add Product
          </Button>
          <div className={css.actionButtons}>
            <Button
              variant="text"
              onClick={() => {
                onFormSubmit(getValues(), true);
              }}
            >
              Save as Draft
            </Button>
            <Button variant="contained" type="submit">
              Save and Proceed
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default InternationalNonConsolidation;
